/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { UtterancePredictionsStore } from 'src/luis/projects/utterances/hooks/useUtterances';

export type UtteranceScoreCellProps = {
    utterancePredictionStore: UtterancePredictionsStore;
    intentName: string;
};

const { styleComponent } = getStylistV2('UtteranceScoreCell');

export const CellContainer = styleComponent<IStackProps>(Stack)('CellContainer', () => ({
    marginTop: '27px'
}));

/**
 * A component rendering the cell with the utterance's
 * intent name and score in the utterances table of the entity page.
 */
export const UtteranceScoreCell = (props: UtteranceScoreCellProps) => {
    const { utterancePredictionStore, intentName } = props;

    const intentScore = utterancePredictionStore.predictionStore?.predictedIntents.find(
        ({ intentId }) => utterancePredictionStore.utteranceStore.labeledIntentId === intentId
    )?.score;

    const displayedScore = intentScore && intentScore === -1 ? '--' : intentScore?.toFixed(3);

    return (
        <CellContainer verticalFill verticalAlign="start" horizontal tokens={{ childrenGap: 10 }}>
            {utterancePredictionStore.utteranceStore.labeledIntentId ? (
                <>
                    <LuisText>{displayedScore}</LuisText>
                    <TruncatedText>
                        <LuisTooltip content={intentName}>{intentName}</LuisTooltip>
                    </TruncatedText>
                </>
            ) : (
                <span>–</span>
            )}
        </CellContainer>
    );
};
